import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where, doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import './SetupGuide.css';

/**
 * Save computed setup-guide progress to Firestore so it persists across sessions
 * and can be read quickly by banners / other components.
 */
export async function saveSetupProgress(uid, completed) {
  const db = getFirestore();
  const setupGuide = {};
  const allSteps = [
    ...STEPS,
    ...OPTIONAL_STEPS,
  ];
  for (const step of allSteps) {
    setupGuide[step.id] = !!completed[step.check];
  }
  setupGuide.allRequiredDone = STEPS.every((s) => !!completed[s.check]);

  // Read existing setupGuide to avoid infinite loop caused by onSnapshot + lastUpdated
  const userRef = doc(db, 'users', uid);
  const existingSnap = await getDoc(userRef);
  const existing = existingSnap.exists ? (existingSnap.data().setupGuide || {}) : {};

  const changed = allSteps.some((step) => setupGuide[step.id] !== !!existing[step.id]) ||
                  setupGuide.allRequiredDone !== !!existing.allRequiredDone;

  if (!changed) {
    console.log('[SetupGuide] No progress change, skipping Firestore write.');
    return;
  }

  setupGuide.lastUpdated = new Date().toISOString();
  await setDoc(userRef, { setupGuide }, { merge: true });
}

const STEPS = [
  { id: 'location', label: 'Create your first location', route: '/admin/locations/new', check: 'locations' },
  { id: 'floorplan', label: 'Create your floorplan', route: '/admin/floor-plan', check: 'tables', requires: ['location'] },
  { id: 'workzones', label: 'Create your workzones', route: '/admin/workzones', check: 'workzones', requires: ['location'] },
  { id: 'category', label: 'Create a category', route: '/admin/categories', check: 'categories', requires: ['location'] },
  { id: 'product', label: 'Create a product', route: '/admin/products', check: 'products', requires: ['category'] },
  { id: 'productOption', label: 'Create a Product Option', route: '/admin/product-options', check: 'productOptions', requires: ['product'] },
  { id: 'team', label: 'Create your team', route: '/admin/team', check: 'employees', requires: ['location'] },
  { id: 'payPeriods', label: 'Create your Pay Periods', route: '/admin/pay-periods', check: 'payPeriods', requires: ['location'] },
  { id: 'shifts', label: 'Create your shifts', route: '/admin/shifts', check: 'shifts', requires: ['location'] },
  { id: 'schedule', label: 'Create your work schedule', route: '/admin/schedule', check: 'schedules', requires: ['team', 'shifts'] },
  { id: 'customer', label: 'Create your first customer', route: '/admin/customers', check: 'customers', requires: [] },
  { id: 'sale', label: 'Create a Sale', route: '/admin/sales', check: 'sales', requires: ['location', 'floorplan', 'workzones', 'product'] },
];

const OPTIONAL_STEPS = [
  { id: 'kds', label: 'Create a KDS', route: '/admin/kds-stations', check: 'kds' },
  { id: 'pos', label: 'Create a POS', route: '/admin/pos', check: 'pos' },
  { id: 'kiosk', label: 'Create a Self Service Kiosk', route: '/admin/vkiosks', check: 'vkiosks' },
  { id: 'publish', label: 'Publish my store to FodyFee Network', route: '/admin/online-ordering', check: 'published' },
  { id: 'fodyflight', label: 'Ask Fody Flight', route: '/admin/fody-flight', check: 'fodyFlight' },
];

function SetupGuide() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [completed, setCompleted] = useState({});
  const [loading, setLoading] = useState(true);

  const checkCompletion = useCallback(async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const db = getFirestore();
    const uid = user.uid;
    const result = {};

    try {
      // Check locations
      const locSnap = await getDocs(collection(db, 'users', uid, 'locations'));
      result.locations = !locSnap.empty;
      const locationIds = locSnap.docs.map((d) => d.id);

      // Check tables (floorplan) — any location
      let hasTables = false;
      for (const locId of locationIds) {
        const tSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'tables'));
        if (!tSnap.empty) { hasTables = true; break; }
      }
      result.tables = hasTables;

      // Check workzones
      let hasWorkzones = false;
      for (const locId of locationIds) {
        const wSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'workzones'));
        if (!wSnap.empty) { hasWorkzones = true; break; }
      }
      result.workzones = hasWorkzones;

      // Check categories
      const catSnap = await getDocs(collection(db, 'users', uid, 'categories'));
      result.categories = !catSnap.empty;

      // Check products
      let hasProducts = false;
      for (const locId of locationIds) {
        const pSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'categories'));
        for (const catDoc of pSnap.docs) {
          const prodSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'categories', catDoc.id, 'products'));
          if (!prodSnap.empty) { hasProducts = true; break; }
        }
        if (hasProducts) break;
      }
      // Also check top-level categories->products
      if (!hasProducts) {
        for (const catDoc of catSnap.docs) {
          const prodSnap = await getDocs(collection(db, 'users', uid, 'categories', catDoc.id, 'products'));
          if (!prodSnap.empty) { hasProducts = true; break; }
        }
      }
      result.products = hasProducts;

      // Check product options
      const optSnap = await getDocs(collection(db, 'users', uid, 'productOptions'));
      result.productOptions = !optSnap.empty;

      // Check employees
      const empSnap = await getDocs(collection(db, 'users', uid, 'employees'));
      result.employees = !empSnap.empty;

      // Check pay periods
      const ppSnap = await getDocs(collection(db, 'users', uid, 'payPeriods'));
      result.payPeriods = !ppSnap.empty;

      // Check shifts
      const shiftSnap = await getDocs(collection(db, 'users', uid, 'shifts'));
      result.shifts = !shiftSnap.empty;

      // Check schedules
      const schedSnap = await getDocs(collection(db, 'users', uid, 'schedules'));
      result.schedules = !schedSnap.empty;

      // Check customers
      const custSnap = await getDocs(collection(db, 'users', uid, 'customers'));
      result.customers = !custSnap.empty;

      // Check sales
      let hasSales = false;
      for (const locId of locationIds) {
        const sSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'sales'));
        if (!sSnap.empty) { hasSales = true; break; }
      }
      if (!hasSales) {
        const sSnap = await getDocs(collection(db, 'users', uid, 'sales'));
        hasSales = !sSnap.empty;
      }
      result.sales = hasSales;

      // Optional checks
      const kdsSnap = await getDocs(collection(db, 'users', uid, 'kds'));
      result.kds = !kdsSnap.empty;

      const posSnap = await getDocs(collection(db, 'users', uid, 'pos'));
      result.pos = !posSnap.empty;

      const vkSnap = await getDocs(collection(db, 'users', uid, 'vkiosks'));
      result.vkiosks = !vkSnap.empty;

      const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', uid)));
      if (!userDoc.empty) {
        const uData = userDoc.docs[0].data();
        result.published = !!uData.storePublished;
        result.fodyFlight = !!uData.fodyFlightEnabled;
      }

      setCompleted(result);
      // Persist to Firestore for cross-session / banner access
      try {
        await saveSetupProgress(uid, result);
      } catch (e) {
        console.warn('[SetupGuide] Failed to persist progress:', e);
      }
    } catch (e) {
      console.error('[SetupGuide] Error checking completion:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkCompletion();

    // Listen for explicit refresh events from admin pages
    const handleRefresh = () => checkCompletion();
    window.addEventListener('setupGuideRefresh', handleRefresh);

    // Also listen to Firestore user doc for external changes
    const auth = getAuth();
    const user = auth.currentUser;
    let unsub = null;
    if (user) {
      const db = getFirestore();
      unsub = onSnapshot(doc(db, 'users', user.uid), () => {
        // Re-check whenever user doc changes (e.g., setupGuide written by another tab)
        checkCompletion();
      });
    }

    return () => {
      window.removeEventListener('setupGuideRefresh', handleRefresh);
      if (unsub) unsub();
    };
  }, [checkCompletion]);

  const isStepAvailable = (step) => {
    if (!step.requires || step.requires.length === 0) return true;
    return step.requires.every((reqId) => {
      const reqStep = STEPS.find((s) => s.id === reqId);
      return reqStep ? !!completed[reqStep.check] : false;
    });
  };

  const handleStepClick = (step) => {
    if (!isStepAvailable(step)) return;
    navigate(step.route);
  };

  const requiredCompleted = STEPS.filter((s) => completed[s.check]).length;
  const requiredTotal = STEPS.length;

  if (loading) return null;
  // Hide guide if all required steps are done
  if (requiredCompleted === requiredTotal) return null;

  return (
    <div className={`setup-guide-container ${collapsed ? 'collapsed' : ''}`}>
      <div className="setup-guide-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="setup-guide-progress">
          <div className="setup-guide-progress-bar" style={{ width: `${(requiredCompleted / requiredTotal) * 100}%` }} />
        </div>
        <span className="setup-guide-title">
          {collapsed ? `Setup Guide (${requiredCompleted}/${requiredTotal})` : 'Setup Guide'}
        </span>
        <span className="setup-guide-toggle">{collapsed ? '+' : '−'}</span>
      </div>

      {!collapsed && (
        <div className="setup-guide-body">
          <div className="setup-guide-section">
            <h4>Required Steps</h4>
            {STEPS.map((step, idx) => {
              const done = completed[step.check];
              const available = isStepAvailable(step);
              return (
                <div
                  key={step.id}
                  className={`setup-guide-item ${done ? 'completed' : ''} ${!available ? 'locked' : ''}`}
                  onClick={() => handleStepClick(step)}
                >
                  <span className="setup-guide-number">{idx + 1}</span>
                  <span className="setup-guide-label">{step.label}</span>
                  {done ? (
                    <span className="setup-guide-check">✓</span>
                  ) : available ? (
                    <span className="setup-guide-arrow">→</span>
                  ) : (
                    <span className="setup-guide-lock">🔒</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="setup-guide-section optional">
            <h4>Optional</h4>
            {OPTIONAL_STEPS.map((step) => {
              const done = completed[step.check];
              return (
                <div
                  key={step.id}
                  className={`setup-guide-item ${done ? 'completed' : ''}`}
                  onClick={() => handleStepClick(step)}
                >
                  <span className="setup-guide-label">{step.label}</span>
                  {done && <span className="setup-guide-check">✓</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default SetupGuide;
